import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdateNetworkChargePointDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/update-network.form-default-values';
import { UpdateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/update-network.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateNetworkMutation } from '@service/cpms-bo-network.service';

import {
  UpdateNetworkContextProvider,
  useNetworkContext,
} from '../network.context';
import { NetworkUpdateFormController } from './network-update-form/network-update-form.controller';

interface NetworkEditControllerProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const NetworkUpdateController = ({
  onSuccess,
  onCancel,
}: NetworkEditControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateNetworkMutation] = useCpmsBoUpdateNetworkMutation();
  const network = useNetworkContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<UpdateNetworkModel, 'network' | 'networkId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(t('$.network-update.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<UpdateNetworkModel, 'network' | 'networkId'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );
    if (!sanitizedObject.network) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const updateNetwork = {
      networkId: fieldValues.networkId,
      network: sanitizedObject.network,
    };

    updateNetworkMutation(updateNetwork)
      .unwrap()
      .then(() => {
        toast.success(t('$.network-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.network-update.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdateNetworkChargePointDefaultValues(network);

  return (
    <UpdateNetworkContextProvider data={data}>
      <NetworkUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateNetworkContextProvider>
  );
};
